import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";
import i18n from "i18next";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
	children: ReactNode;
}

interface State {
	error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): State {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error("Unhandled error:", error, info.componentStack);
		Sentry.captureException(error, {
			contexts: {
				react: {
					componentStack: info.componentStack ?? undefined,
				},
			},
		});
	}

	render() {
		if (this.state.error) {
			return (
				<div className="flex min-h-screen items-center justify-center bg-background">
					<div className="mx-auto max-w-md space-y-4 rounded-lg border p-8 text-center">
						<h1 className="text-lg font-semibold">
							{i18n.t("error_boundary_title")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{i18n.t("error_boundary_description")}
						</p>
						<pre className="max-h-32 overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
							{this.state.error.message}
						</pre>
						<Button
							onClick={() => {
								this.setState({ error: null });
								window.location.href = "/";
							}}
						>
							{i18n.t("reload")}
						</Button>
					</div>
				</div>
			);
		}
		return this.props.children;
	}
}
